import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Avatar from './Avatar';
import { hueOf } from '../lib/initials';
import { deleteMessage, editMessage, messageVersions } from '../lib/chatLink';
import { mentionsIn } from '../lib/mentions';
import { fmtDate, fmtTime } from '../lib/when';

/**
 * A message row's extras from the server (084), in the names the timeline
 * uses. Every read of a conversation or a branch goes through this, so a
 * server from before 084 - which sends none of them - draws as it did.
 */
export const extrasOf = (m) => ({
  mentions: Array.isArray(m?.mentions) ? m.mentions : [],
  edited: m?.edited === true,
  deleted: m?.deleted === true,
  branch: m?.branch && m.branch.count > 0 ? m.branch : null,
  replyTo: m?.reply_to || null,
  parentId: m?.reply_to_id || null,
  mayEdit: m?.may_edit === true,
  mayDelete: m?.may_delete === true,
  // 096: the branch off this message is answered / was reopened / was moved
  // to a topic; or this message is the copy that opened a topic.
  answered: m?.answered || null,
  reopened: m?.reopened || null,
  moved: m?.moved || null,
  movedFrom: m?.moved_from || null
});

/**
 * Editing and deleting the messages in `setMessages`' list: which one is
 * being edited, saving it (with the @mentions its new words make, from
 * `people`), and deleting one - shown at once, put right by the server.
 */
export function useMessageEdits(setMessages, onTrouble, people) {
  const [editing, setEditing] = useState(null);
  const peopleRef = useRef(people);
  useEffect(() => { peopleRef.current = people; }, [people]);
  const onEdit = useCallback((m) => setEditing(m.id), []);
  const onEditCancel = useCallback(() => setEditing(null), []);
  const onEditSave = useCallback(async (m, body) => {
    const got = await editMessage(m.scope, m.id, body, mentionsIn(body, peopleRef.current || []));
    setMessages((prev) => prev.map((x) => (x.id === m.id && x.scope === m.scope
      ? { ...x, body: got?.body ?? body, edited: true, mentions: Array.isArray(got?.mentions) ? got.mentions : [] } : x)));
    setEditing(null);
  }, [setMessages]);
  const onDelete = useCallback(async (m) => {
    try {
      await deleteMessage(m.scope, m.id);
      setMessages((prev) => prev.map((x) => (x.id === m.id && x.scope === m.scope
        ? { ...x, deleted: true, body: '', files: [], mentions: [], edited: false, mayEdit: false, mayDelete: false } : x)));
    } catch (err) {
      onTrouble(err.response?.data?.error || 'That message was not deleted');
    }
  }, [setMessages, onTrouble]);
  return { editing, onEdit, onEditCancel, onEditSave, onDelete, setEditing };
}

/** A change from the live poll (084), applied to a list of messages. */
export const applyChanges = (list, changes) => {
  if (!Array.isArray(changes) || !changes.length) return list;
  const by = new Map(changes.map((c) => [`${c.scope}:${c.id}`, c]));
  let moved = false;
  const next = list.map((m) => {
    const c = by.get(`${m.scope}:${m.id}`);
    if (!c) return m;
    moved = true;
    return c.deleted
      ? { ...m, deleted: true, body: '', files: [], mentions: [], edited: false, mayEdit: false, mayDelete: false }
      : { ...m, body: c.body, mentions: c.mentions || [], edited: c.edited === true };
  });
  return moved ? next : list;
};

/**
 * The small pieces a message grew in migration 084, beside the smiley:
 *
 *   ↩  BRANCH OFF - answer this one message on its own, in a panel. Gordon
 *      asked for it right next to the faint smiley, with the same rules: there
 *      on hover or keyboard focus on a desktop, faintly there on a touch
 *      screen, and in the long-press sheet.
 *   ⋯  EDIT OR DELETE my own (an administrator: delete anybody's), as a small
 *      menu by the message - never across the screen.
 *
 * And what they leave behind: the "3 in branch" line under a message with a
 * branch, "(edited)" opening the earlier wordings, and the box a message is
 * edited in, in place.
 *
 * Styled from pages/ChatPage.css, `.ch-` like the rest of chat.
 */

const narrow = () => {
  try { return window.matchMedia('(max-width: 767px)').matches; } catch { return false; }
};

const clock = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/** ↩ - a branch off the line. */
export const BranchIcon = ({ size = 16 }) => (
  <svg className="ch-branch-icon" viewBox="0 0 16 16" width={size} height={size} aria-hidden="true" focusable="false">
    <path d="M6 3L2.5 6.5 6 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
      strokeLinejoin="round" />
    <path d="M2.8 6.5H9a4 4 0 014 4V13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const MoreIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 16 16" width={size} height={size} aria-hidden="true" focusable="false">
    <circle cx="3.5" cy="8" r="1.4" fill="currentColor" />
    <circle cx="8" cy="8" r="1.4" fill="currentColor" />
    <circle cx="12.5" cy="8" r="1.4" fill="currentColor" />
  </svg>
);

/** "1 in branch" / "3 in branch". */
export const inBranch = (n) => `${n} in branch`;

/** A tick - a branch marked answered (096). */
export const TickIcon = ({ size = 14 }) => (
  <svg className="ch-tick-icon" viewBox="0 0 16 16" width={size} height={size} aria-hidden="true" focusable="false">
    <path d="M3 8.5l3.2 3.2L13 4.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
      strokeLinejoin="round" />
  </svg>
);

/** Lines going into a topic - a branch moved to one (096). */
export const TopicIcon = ({ size = 14 }) => (
  <svg className="ch-topic-icon" viewBox="0 0 16 16" width={size} height={size} aria-hidden="true" focusable="false">
    <path d="M2.5 4.5h11M2.5 8h7M2.5 11.5h5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M11 9.5l2.5 2-2.5 2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
      strokeLinejoin="round" />
  </svg>
);

/** "Marked answered by Rita Borg · 14:02" - the small line the panel draws (Malta time, en-GB). */
export const answeredLine = (x, reopened = false) => `${reopened ? 'Reopened' : 'Marked answered'} by ${x?.by_name || 'somebody'} · ${fmtTime(x?.at)}`;

/**
 * "Moved to topic Boxes" - under a message whose branch became a topic
 * (096), in place of "3 in branch". Opens the topic.
 */
export function MovedChip({ m, onOpen }) {
  const t = m.moved;
  return (
    <button type="button" className="ch-moved-chip" title={`Moved by ${t.by_name || 'somebody'} · ${fmtTime(t.at)}`}
      aria-label={`Moved to topic ${t.topic || ''}. Open the topic`} onClick={onOpen}>
      <TopicIcon size={13} />
      <span className="ch-moved-text">Moved to topic <strong>{t.topic || 'a topic'}</strong></span>
    </button>
  );
}

/**
 * Over the copy that opens a topic a branch was moved to (096): where it
 * came from, and a way back to the message it was copied from.
 */
export function MovedFromNote({ m, onOpen }) {
  const f = m.movedFrom;
  const text = (
    <span className="ch-moved-from-text">
      Moved from the conversation · originally by {f.author_name || 'somebody'}, {fmtDate(f.created_at)}
    </span>
  );
  return onOpen ? (
    <button type="button" className="ch-moved-from" onClick={onOpen} title="Open the original message">
      <TopicIcon size={12} />{text}
    </button>
  ) : (
    <span className="ch-moved-from"><TopicIcon size={12} />{text}</span>
  );
}

/**
 * Under a message with a branch: up to three faces of who answered, "3 in
 * branch", when the last answer was, and "2 new" in the accent when some are
 * unread for me. The whole line opens the branch.
 */
export function BranchLine({ m, onOpen }) {
  const b = m.branch;
  const unread = Number(b.unread) || 0;
  const answered = m.answered || null;
  return (
    <button type="button" className="ch-branch-line" data-unread={unread > 0 || undefined}
      data-answered={answered ? 'true' : undefined}
      aria-label={`${inBranch(b.count)}${answered ? ', answered' : ''}${unread ? `, ${unread} new` : ''}. Open the branch`}
      onClick={onOpen}>
      <span className="ch-branch-faces" aria-hidden="true">
        {(b.people || []).slice(0, 3).map((p) => (
          <Avatar key={p.key} person={p} className="ch-avatar ch-branch-face" data-hue={hueOf(p.key)} size={20} />
        ))}
      </span>
      <span className="ch-branch-count">{inBranch(b.count)}</span>
      {b.last_at && <span className="ch-branch-when">{clock(b.last_at)}</span>}
      {answered && (
        <span className="ch-branch-answered" title={answeredLine(answered)}>
          <TickIcon size={12} /> Answered
        </span>
      )}
      {unread > 0 && <span className="ch-branch-new">{unread} new</span>}
    </button>
  );
}

/** A popover by `anchor` on a desktop, kept on screen; a sheet on a phone. */
function usePlaced(anchor, panel, { width = 260, height = 160 } = {}) {
  const [sheet] = useState(() => narrow() || !anchor);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (sheet || !anchor) return undefined;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const w = panel.current ? panel.current.offsetWidth : width;
      const h = panel.current ? panel.current.offsetHeight : height;
      const left = Math.max(8, Math.min(a.right - w, window.innerWidth - w - 8));
      const below = a.bottom + 6 + h <= window.innerHeight - 8 || a.top < h + 14;
      setPos(below ? { left, top: a.bottom + 6 } : { left, bottom: window.innerHeight - a.top + 6 });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor, sheet, panel, width, height]);
  const style = sheet ? undefined : (pos ? { left: pos.left, top: pos.top, bottom: pos.bottom }
    : { left: 0, top: 0, visibility: 'hidden' });
  return { sheet, style, placed: sheet || pos !== null };
}

/** Escape and a click elsewhere close it; the focus goes back to what opened it. */
function useDismiss(panel, anchor, onClose) {
  useEffect(() => {
    const away = (e) => {
      if (panel.current && panel.current.contains(e.target)) return;
      if (anchor && anchor.contains && anchor.contains(e.target)) return;
      onClose();
    };
    const esc = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('mousedown', away, true);
    document.addEventListener('keydown', esc, true);
    return () => {
      document.removeEventListener('mousedown', away, true);
      document.removeEventListener('keydown', esc, true);
      if (anchor && document.contains(anchor)) anchor.focus({ preventScroll: true });
    };
  }, [panel, anchor, onClose]);
}

const deleteNote = (m) => (m.mine
  ? 'Everybody in the conversation will see “This message was deleted” in its place.'
  : `This is ${m.author || 'somebody'}’s message. Deleting it is recorded in the Audit Log.`);

/**
 * THE ⋯ MENU: Edit and Delete, by the message. Delete asks again, in the
 * menu itself - no browser confirm box - and says what will happen.
 */
export function MessageMenu({ m, react, anchor, onClose }) {
  const panel = useRef(null);
  const first = useRef(null);
  const [confirm, setConfirm] = useState(false);
  const { sheet, style, placed } = usePlaced(anchor, panel, { width: 230, height: 110 });
  useDismiss(panel, anchor, onClose);
  useEffect(() => { if (placed) first.current?.focus({ preventScroll: true }); }, [placed, confirm]);
  const move = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = [...panel.current.querySelectorAll('button')];
    const at = items.indexOf(document.activeElement);
    const next = items[(at + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length];
    if (next) { e.preventDefault(); next.focus(); }
  };
  return createPortal(
    <>
      {sheet && <div className="ch-ep-scrim" aria-hidden="true" onClick={onClose} />}
      <div ref={panel} className="ch-msg-menu" data-sheet={sheet || undefined} role={confirm ? 'alertdialog' : 'menu'}
        aria-label={confirm ? 'Delete this message?' : 'Message'} style={style} onKeyDown={move}>
        {!confirm ? (
          <>
            {m.mayEdit && react.onEdit && (
              <button ref={first} type="button" role="menuitem" onClick={() => { onClose(); react.onEdit(m); }}>
                <span aria-hidden="true" className="ch-sheet-glyph">✎</span> Edit
              </button>
            )}
            {m.mayDelete && react.onDelete && (
              <button ref={m.mayEdit ? undefined : first} type="button" role="menuitem" className="ch-menu-danger"
                onClick={() => setConfirm(true)}>
                <span aria-hidden="true" className="ch-sheet-glyph">🗑</span> Delete
              </button>
            )}
          </>
        ) : (
          <div className="ch-confirm">
            <p className="ch-confirm-title">Delete this message?</p>
            <p className="ch-confirm-note">{deleteNote(m)}</p>
            <div className="ch-confirm-actions">
              <button type="button" className="ch-flat ch-small" onClick={onClose}>Cancel</button>
              <button ref={first} type="button" className="ch-danger ch-small"
                onClick={() => { onClose(); react.onDelete(m); }}>Delete</button>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body
  );
}

/** The same question, from a phone's long press: a sheet of its own. */
export function DeleteConfirm({ m, onConfirm, onClose }) {
  const panel = useRef(null);
  const first = useRef(null);
  useDismiss(panel, null, onClose);
  useEffect(() => { first.current?.focus({ preventScroll: true }); }, []);
  return createPortal(
    <>
      <div className="ch-ep-scrim" aria-hidden="true" onClick={onClose} />
      <div ref={panel} className="ch-msg-menu" data-sheet="true" role="alertdialog" aria-label="Delete this message?">
        <div className="ch-confirm">
          <p className="ch-confirm-title">Delete this message?</p>
          <p className="ch-confirm-note">{deleteNote(m)}</p>
          <div className="ch-confirm-actions">
            <button type="button" className="ch-flat ch-small" onClick={onClose}>Cancel</button>
            <button ref={first} type="button" className="ch-danger ch-small" onClick={onConfirm}>Delete</button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

/**
 * "(edited)", opened: what it says now, and every earlier wording with when
 * it was replaced - for the people in the conversation. A deleted message has
 * none to show anybody (that is the audited review's).
 */
export function Versions({ m, anchor, onClose }) {
  const panel = useRef(null);
  const [got, setGot] = useState(null);
  const [error, setError] = useState(null);
  const { sheet, style } = usePlaced(anchor, panel, { width: 300, height: 200 });
  useDismiss(panel, anchor, onClose);
  useEffect(() => {
    let live = true;
    messageVersions(m.scope, m.id)
      .then((r) => { if (live) setGot(r); })
      .catch((err) => { if (live) setError(err.response?.data?.error || 'Could not load the earlier versions'); });
    return () => { live = false; };
  }, [m.scope, m.id]);
  return createPortal(
    <>
      {sheet && <div className="ch-ep-scrim" aria-hidden="true" onClick={onClose} />}
      <div ref={panel} className="ch-versions" data-sheet={sheet || undefined} role="dialog"
        aria-label="Earlier versions" style={style} tabIndex={-1}>
        <div className="ch-versions-head">
          <span>Edited</span>
          <button type="button" className="ch-flat ch-small" onClick={onClose}>Close</button>
        </div>
        {error && <p className="ch-attach-error" role="alert">{error}</p>}
        {!got && !error && <p className="ch-empty">Loading…</p>}
        {got && (
          <ol className="ch-versions-list">
            <li className="ch-version" data-now="true">
              <span className="ch-version-when">Now{got.edited_at ? ` · since ${clock(got.edited_at)}` : ''}</span>
              <span className="ch-version-body">{got.body}</span>
            </li>
            {[...(got.versions || [])].reverse().map((v, i) => (
              <li className="ch-version" key={`${v.replaced_at}-${i}`}>
                <span className="ch-version-when">Until {clock(v.replaced_at)}</span>
                <span className="ch-version-body">{v.body}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </>,
    document.body
  );
}

/**
 * A message being edited, in place of its bubble: Enter saves (Shift+Enter
 * a new line), Escape puts it back as it was. The cursor starts at the end.
 */
export function EditBox({ m, react }) {
  const [text, setText] = useState(m.body || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const box = useRef(null);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [text]);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    const n = el.value.length;
    try { el.setSelectionRange(n, n); } catch { /* fine */ }
  }, []);
  const save = async () => {
    const body = text.trim();
    if (!body || busy) return;
    if (body === String(m.body || '').trim()) { react.onEditCancel(); return; }
    setBusy(true);
    setError(null);
    try {
      await react.onEditSave(m, body);
    } catch (err) {
      setError(err.response?.data?.error || 'That change did not save');
      setBusy(false);
    }
  };
  return (
    <div className="ch-edit">
      <label className="ch-say-label" htmlFor={`ch-edit-${m.id}`}>Edit your message</label>
      <textarea
        ref={box}
        id={`ch-edit-${m.id}`}
        className="ch-field ch-edit-box"
        rows={1}
        value={text}
        disabled={busy}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); react.onEditCancel(); return; }
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); save(); }
        }}
      />
      <div className="ch-edit-actions">
        <span className="ch-edit-hint">Enter saves · Escape cancels</span>
        <button type="button" className="ch-flat ch-small" onClick={() => react.onEditCancel()} disabled={busy}>Cancel</button>
        <button type="button" className="ch-primary ch-small" onClick={save} disabled={busy || !text.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
      {error && <p className="ch-attach-error" role="alert">{error}</p>}
    </div>
  );
}
